import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import MatchCard from "@/components/MatchCard";
import { api } from "@/lib/api";
import { Calendar as CalendarIcon, Search, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtUAEDateOnly } from "@/lib/dates";

// Return YYYY-MM-DD string of an ISO kickoff in UAE timezone (Asia/Dubai = UTC+4, no DST)
function uaeDateKey(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  // Add 4h offset to get UAE local time
  const uae = new Date(d.getTime() + 4 * 60 * 60 * 1000);
  return `${uae.getUTCFullYear()}-${String(uae.getUTCMonth() + 1).padStart(2, "0")}-${String(uae.getUTCDate()).padStart(2, "0")}`;
}

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [group, setGroup] = useState("all");
  const [stage, setStage] = useState("all");
  const [query, setQuery] = useState("");
  const [pickedDate, setPickedDate] = useState(null);

  const load = async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/matches"),
        api.get("/predictions/mine"),
      ]);
      setMatches(m.data);
      setPredictions(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const predByMatch = useMemo(
    () => predictions.reduce((acc, p) => { acc[p.match_id] = p; return acc; }, {}),
    [predictions]
  );

  const groups = useMemo(() => {
    const set = new Set(matches.map(m => m.group).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [matches]);

  const stages = useMemo(() => {
    // Bucket each match into a tournament phase
    const phaseOf = (m) => {
      if (m.group) return "Group Stage";
      const s = (m.stage || "").toLowerCase();
      if (s.includes("round of 32")) return "Round of 32";
      if (s.includes("round of 16")) return "Round of 16";
      if (s.includes("quarter")) return "Quarter-final";
      if (s.includes("semi")) return "Semi-final";
      if (s.includes("3rd")) return "3rd Place";
      if (s.includes("final")) return "Final";
      return m.stage || "Other";
    };
    const set = new Set(matches.map(phaseOf));
    return ["all", ...Array.from(set)];
  }, [matches]);

  // Set of dates (UAE local) that have matches — used to highlight calendar
  const matchDatesUAE = useMemo(() => {
    const s = new Set();
    matches.forEach((m) => { if (m.kickoff) s.add(uaeDateKey(m.kickoff)); });
    return s;
  }, [matches]);

  const pickedKey = pickedDate ? `${pickedDate.getFullYear()}-${String(pickedDate.getMonth() + 1).padStart(2, "0")}-${String(pickedDate.getDate()).padStart(2, "0")}` : null;

  const filtered = matches.filter((m) => {
    const statusOk = filter === "all" ? true : filter === "upcoming" ? m.status === "upcoming" : m.status === "finished";
    const groupOk = group === "all" ? true : m.group === group;
    let stageOk = true;
    if (stage !== "all") {
      const s = (m.stage || "").toLowerCase();
      const target = stage.toLowerCase();
      if (stage === "Group Stage") stageOk = !!m.group || s.startsWith("group stage");
      else if (target === "round of 32") stageOk = s === "round of 32";
      else if (target === "round of 16") stageOk = s === "round of 16";
      else if (target === "quarter-final") stageOk = s === "quarter-final";
      else if (target === "semi-final") stageOk = s === "semi-final";
      else if (target === "3rd place") stageOk = s === "3rd place";
      else if (target === "final") stageOk = s === "final";
      else stageOk = s.includes(target);
    }
    const q = query.trim().toLowerCase();
    const searchOk = !q
      ? true
      : (m.home_team || "").toLowerCase().includes(q) || (m.away_team || "").toLowerCase().includes(q);
    const dateOk = !pickedKey ? true : uaeDateKey(m.kickoff) === pickedKey;
    return statusOk && groupOk && stageOk && searchOk && dateOk;
  });

  return (
    <div className="min-h-screen">
      <Navbar />

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10"
        >
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-lavender mb-3">The Fixtures</div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tight uppercase leading-none">
              Call The <span className="text-purple">Score</span>
            </h1>
            <p className="text-white/60 mt-3 max-w-xl text-sm">
              Exact score <span className="text-lavender font-bold">+5</span>,
              right winner <span className="text-lavender font-bold">+3</span>,
              correct BTTS <span className="text-lavender font-bold">+1</span>. Max <span className="text-lavender font-bold">9</span> per game ·
              <span className="text-purple"> locks 2h before kickoff (GST)</span>.
            </p>
          </div>

          <div className="flex gap-2 p-1 rounded-full border border-white/10 bg-surface/60 self-start">
            {[
              { k: "upcoming", l: "Upcoming" },
              { k: "finished", l: "Finished" },
              { k: "all", l: "All" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`filter-${f.k}`}
                className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-colors ${
                  filter === f.k ? "bg-gradient-to-r from-vivid to-purple text-white font-bold" : "text-white/60 hover:text-white"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </motion.div>

        {/* 90-minute scoring disclaimer */}
        <div className="mb-8 p-4 rounded-2xl bg-lavender/5 border border-lavender/30 flex items-start gap-3" data-testid="scoring-disclaimer">
          <div className="text-lavender shrink-0 mt-0.5">⚖️</div>
          <div className="text-xs sm:text-sm text-white/70 leading-relaxed">
            <span className="text-lavender font-bold tracking-wider uppercase">Scoring policy ·</span>{" "}
            All points are awarded on the <span className="text-lavender font-semibold">90-minute regulation result</span>.
            Extra time and penalty shoot-outs are not counted. Predictions auto-lock 2 hours before kickoff (GST / UAE time).
          </div>
        </div>

        {/* Search by team + filter by date */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team (e.g. Morocco, Mexico…)"
              data-testid="matches-search"
              className="w-full bg-surface border border-white/10 rounded-full pl-11 pr-10 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-purple focus:ring-1 focus:ring-purple outline-none transition-all"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                data-testid="matches-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-lavender p-1"
              >
                <X size={14}/>
              </button>
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="matches-date-trigger"
                className={`btn-bounce inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm tracking-wider transition-colors ${
                  pickedDate
                    ? "bg-gradient-to-r from-vivid to-purple text-white border-purple font-bold"
                    : "bg-surface border-white/10 text-white/70 hover:border-purple"
                }`}
              >
                <CalendarIcon size={15}/>
                {pickedDate ? fmtUAEDateOnly(pickedDate.toISOString()) : "Filter by date"}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-auto p-0 bg-surface border-purple/30 text-white shadow-[0_18px_60px_-10px_rgba(160,64,192,0.45)]"
              data-testid="matches-date-popover"
            >
              <Calendar
                mode="single"
                selected={pickedDate}
                onSelect={(d) => setPickedDate(d || null)}
                defaultMonth={pickedDate || new Date("2026-06-11")}
                modifiers={{ hasMatch: (date) => matchDatesUAE.has(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`) }}
                modifiersClassNames={{ hasMatch: "match-day" }}
                className="bg-transparent"
              />
              <div className="flex items-center justify-between border-t border-white/10 px-3 py-2 text-xs">
                <span className="text-white/40 tracking-widest uppercase">{pickedDate ? "1 day selected" : "Pick a date"}</span>
                <button
                  type="button"
                  onClick={() => setPickedDate(null)}
                  data-testid="matches-date-clear"
                  className="text-lavender hover:text-purple font-bold tracking-wider uppercase"
                  disabled={!pickedDate}
                >
                  Clear
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Stage filter chips */}
        {stages.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {stages.map((s) => (
              <button
                key={s}
                onClick={() => setStage(s)}
                data-testid={`stage-${s.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-xs tracking-[0.2em] uppercase border transition-all ${
                  stage === s
                    ? "bg-gradient-to-r from-vivid to-purple text-white border-purple font-bold"
                    : "border-white/10 text-white/50 hover:text-white hover:border-purple"
                }`}
              >
                {s === "all" ? "All Stages" : s}
              </button>
            ))}
          </div>
        )}

        {/* Group filter chips */}
        {groups.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {groups.map((g) => (
              <button
                key={g}
                onClick={() => setGroup(g)}
                data-testid={`group-${g}`}
                className={`px-4 py-1.5 rounded-full text-xs tracking-[0.2em] uppercase border transition-all ${
                  group === g
                    ? "bg-lavender text-plum border-lavender font-bold"
                    : "border-white/10 text-white/50 hover:text-white hover:border-purple"
                }`}
              >
                {g === "all" ? "All Groups" : `Group ${g}`}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm tracking-widest uppercase">Loading fixtures…</div>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {filtered.map((m) => (
              <MatchCard
                key={m.id}
                match={m}
                prediction={predByMatch[m.id]}
                onUpdated={load}
              />
            ))}
          </motion.div>
        )}
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-20 max-w-md mx-auto">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-surface border border-white/5 mb-6">
        <CalendarIcon className="text-white/30" size={32} />
      </div>
      <div className="font-display text-3xl uppercase tracking-tight">No Fixtures Here</div>
      <p className="text-white/40 text-sm mt-3">
        Try a different filter or group. If the fixtures aren&apos;t loading, contact the admin.
      </p>
    </div>
  );
}
